/**
 * The SPA's `middleware.ts`.
 *
 * `middleware.ts` decides, for one pathname and one session, whether the
 * request proceeds or is redirected. This module makes the SAME decision as a
 * PURE FUNCTION, so it can be unit-tested against the original's four branches
 * rather than asserted by clicking around.
 *
 * The original, in order (see `middleware.ts`):
 *
 *   1. Public routes pass with no session check:
 *      `/login`, `/api/auth/*`, `/api/cron/*`, `/access-request-pending`,
 *      `/api/access-requests/handle`.
 *   2. No session -> redirect to `/login`.
 *   3. `/admin*`, `/settings`, `/settings/*`, `/api/admin*` are HARD role
 *      checks, not matrix-gated: role must be ADMIN, else
 *      `/no-access?page=admin`. Note the ordering — an admin path RETURNS
 *      here, so it never reaches the registry gate below.
 *   4. Otherwise, if `findPageByPath` maps the path to a registry entry,
 *      `resolvePermission(view)` must allow it, else
 *      `/no-access?page=<key>`.
 *   5. Anything not in the registry and not admin-prefixed passes with only
 *      the session check — `/no-access` itself, for instance.
 *
 * Faithfulness matters more than elegance here: SAFE-04 says no page may
 * become newly reachable or newly denied, and this function is where that is
 * either true or false. `tests/route-guard.test.ts` walks every branch,
 * including the ones the SPA does not currently route (the `/api/*` prefixes),
 * because S2 must be able to add those routes without re-deriving the rule.
 */
import { findPageByPath, type PageKey } from "./pages";
import {
  resolvePermission,
  type PagePermissionRow,
  type Role,
} from "./permissions";

export type GuardDecision =
  | { kind: "allow" }
  /** Not signed in — send to `/login`. */
  | { kind: "login" }
  /** Signed in but denied — send to `/no-access?page=<page>`. */
  | { kind: "no-access"; page: PageKey | "admin" }
  /**
   * Signed in, role USER, and the permission map has not arrived yet. NOT a
   * denial: rendering a denial here would tell a user they lost access when
   * all that happened is a failed fetch. The router shows a loading/retry
   * state instead. Admins never reach this — they short-circuit.
   */
  | { kind: "permissions-unknown" };

export interface GuardSession {
  role: Role;
}

/** Exactly the list in `middleware.ts`. */
export function isPublicPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname.startsWith("/api/auth") ||
    pathname.startsWith("/api/cron") ||
    pathname === "/access-request-pending" ||
    pathname === "/api/access-requests/handle"
  );
}

/**
 * Paths that LOOK admin-prefixed but are every user's own business.
 *
 * `/settings/recycle-bin` is the whole list. It sits under `/settings` for URL
 * tidiness, but the bin it shows is per-user: `GET /py-api/v1/recycle-bin`
 * answers a USER with `{"scope":"own"}` and their own soft-deleted rows. With
 * the blanket `/settings/` rule, a non-admin could soft-delete their own record
 * and then never restore it — the 30-day undo existed for six of eight people
 * only in the sense that it silently ran out. Observed 2026-08-22.
 *
 * Anything added here must be per-user by construction on the SERVER, not just
 * scoped in the UI.
 */
const OWN_BUSINESS_UNDER_SETTINGS = new Set<string>(["/settings/recycle-bin"]);

/** Exactly the admin branch in `middleware.ts`, including `/api/admin`. */
export function isAdminOnlyPath(pathname: string): boolean {
  if (OWN_BUSINESS_UNDER_SETTINGS.has(pathname)) return false;
  return (
    pathname.startsWith("/admin") ||
    pathname === "/settings" ||
    pathname.startsWith("/settings/") ||
    pathname.startsWith("/api/admin")
  );
}

export function guardRoute(
  pathname: string,
  session: GuardSession | null,
  permissions: Record<string, PagePermissionRow> | null,
): GuardDecision {
  if (isPublicPath(pathname)) return { kind: "allow" };

  if (!session) return { kind: "login" };

  if (isAdminOnlyPath(pathname)) {
    return session.role === "ADMIN" ? { kind: "allow" } : { kind: "no-access", page: "admin" };
  }

  const page = findPageByPath(pathname);
  if (!page) return { kind: "allow" };

  // A USER whose map has not loaded is UNKNOWN, not denied — see the
  // `permissions-unknown` note above. An ADMIN is decided without the map.
  if (session.role !== "ADMIN" && permissions === null) {
    return { kind: "permissions-unknown" };
  }

  const { allowed } = resolvePermission(session.role, permissions, page.key, "view");
  return allowed ? { kind: "allow" } : { kind: "no-access", page: page.key };
}

/** The URL a decision redirects to, or null when the route may render. */
export function redirectFor(decision: GuardDecision): string | null {
  switch (decision.kind) {
    case "allow":
    case "permissions-unknown":
      return null;
    case "login":
      return "/login";
    case "no-access":
      return `/no-access?page=${encodeURIComponent(decision.page)}`;
  }
}
